import {
  createFileRoute,
  Outlet,
  useRouteContext,
} from '@tanstack/react-router';

import { UserRoles } from '@repo/app-constants';
import { isSessionExpiredError } from '@repo/api-client';
import { checkUserRoles } from '@repo/utils';
import { redirect } from '@tanstack/react-router';
import { createServerFn, ServerFn } from '@tanstack/react-start';
import { Auth } from '~/api';
import AuthedLayout from '~/components/layouts/AuthedLayout/AuthedLayout';
import Login from '~/components/Login/Login';
import { currentPartnerQueryOptions } from '~/services/partner';
import { setJWTToken } from '~/utils/jwt';
import { SignInFormValues } from '../components/Login/SignInForm/SignInForm';
import { AuthResponse } from '../services/auth';

// Create the server function with validator
const loginServerFn = createServerFn({ method: 'POST' })
  .validator((d: SignInFormValues) => d)
  .handler(async ({ data }) => {
    try {
      const authResponse: AuthResponse = await Auth.signIn(data);

      if (!authResponse.user || !authResponse.token)
        throw new Error('Incorrect username or password.');

      const hasRequiredRole = checkUserRoles(
        authResponse?.user?.roles,
        UserRoles.PARTNER,
      );

      if (!hasRequiredRole) throw new Error('Invalid email or password');

      const { getWebRequest } = await import('@tanstack/react-start/server');
      const request = getWebRequest();
      const responseHeaders = new Headers();
      const context = { request, responseHeaders };

      setJWTToken(authResponse.token, {}, context);

      throw redirect({
        to: '/',
        headers: responseHeaders,
      });
    } catch (err) {
      throw err;
    }
  });

// Export with a direct type assertion
export const loginFn = loginServerFn as unknown as ServerFn<
  'POST',
  'data',
  undefined,
  SignInFormValues,
  AuthResponse
>;

const AuthedComponent = () => {
  const context = useRouteContext({
    from: '__root__',
  });

  return context.isAuthenticated ? (
    <AuthedLayout>
      <Outlet />
    </AuthedLayout>
  ) : (
    <Login />
  );
};
export const Route = createFileRoute('/_authed')({
  loader: async ctx => {
    try {
      const partner = await ctx.context.queryClient.ensureQueryData(
        currentPartnerQueryOptions(ctx.context.isAuthenticated),
      );
      return { partner };
    } catch (error) {
      // A thrown redirect also works during SSR, where `window` is absent.
      if (isSessionExpiredError(error)) {
        throw redirect({ to: '/logout' });
      }
      throw error;
    }
  },
  component: AuthedComponent,
});
